import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import SeoManager from './components/SeoManager';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import MyEAs from './pages/MyEAs';
import MagicNumbers from './pages/MagicNumbers';
import Trades from './pages/Trades';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import ExpenseTracker from './pages/ExpenseTracker';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import VerifyEmail from './pages/auth/VerifyEmail';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ApiKeys from './pages/settings/ApiKeys';
import EaDownload from './pages/settings/EaDownload';
import Upgrade from './pages/settings/Upgrade';
import PaymentHistory from './pages/settings/PaymentHistory';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminBilling from './pages/admin/AdminBilling';
import AdminApiKeys from './pages/admin/AdminApiKeys';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminLogs from './pages/admin/AdminLogs';
import AdminFeatureFlags from './pages/admin/AdminFeatureFlags';
import AdminPricing from './pages/admin/AdminPricing';
import AdminWallets from './pages/admin/AdminWallets';
import AdminPayments from './pages/admin/AdminPayments';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminEAStore from './pages/admin/AdminEAStore';
import AdminRoute from './components/AdminRoute';
import AdminSidebar from './components/AdminSidebar';
import LandingPage from './pages/public/LandingPage';
import FeaturesPage from './pages/public/FeaturesPage';
import PricingPage from './pages/public/PricingPage';
import HowItWorksPage from './pages/public/HowItWorksPage';
import SmartHome from './pages/public/SmartHome';
import ExplorePage from './pages/public/ExplorePage';
import SharedAnalysis from './pages/public/SharedAnalysis';
import EAStorePage from './pages/public/EAStorePage';
import EAStoreDetail from './pages/public/EAStoreDetail';
import LegalPage from './pages/public/LegalPage';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { WebSocketProvider } from './hooks/useWebSocket';

function AppLayout({ children }) {
  const location = useLocation();
  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <div className="flex min-h-screen p-3 gap-3" style={{ background: 'var(--bg-deepest, #080b12)' }}>
        <Sidebar />
        <main id="main-content" className="flex-1 lg:ml-[222px] pt-16 lg:pt-6 bg-[var(--bg-main,#0d1117)] rounded-2xl border border-[var(--border-color,#21262d)] p-4 lg:px-7 lg:pb-6 overflow-auto shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <ErrorBoundary>
            <div key={location.pathname} className="page-transition">
              {children}
            </div>
          </ErrorBoundary>
        </main>
      </div>
    </>
  );
}

function AdminLayout({ children }) {
  const location = useLocation();
  return (
    <div className="flex min-h-screen p-3 gap-3" style={{ background: 'var(--bg-deepest, #080b12)' }}>
      <AdminSidebar />
      <main className="flex-1 lg:ml-[222px] pt-16 lg:pt-6 bg-[var(--bg-main,#0d1117)] rounded-2xl border border-[var(--border-color,#21262d)] p-4 lg:px-7 lg:pb-6 overflow-auto shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <ErrorBoundary>
          <div key={location.pathname} className="page-transition">
            {children}
          </div>
        </ErrorBoundary>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <WebSocketProvider>
        <BrowserRouter>
          <SeoManager />
          <ErrorBoundary>
            <Routes>
              {/* Smart home — landing if not logged in, dashboard if logged in */}
              <Route path="/" element={<SmartHome />} />

              {/* Public marketing pages */}
              <Route path="/home" element={<LandingPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/shared/:token" element={<SharedAnalysis />} />
              <Route path="/ea-store" element={<EAStorePage />} />
              <Route path="/ea-store/:slug" element={<EAStoreDetail />} />
              <Route path="/privacy" element={<LegalPage />} />
              <Route path="/terms" element={<LegalPage />} />
              <Route path="/refund-policy" element={<LegalPage />} />
              <Route path="/risk-disclaimer" element={<LegalPage />} />
              <Route path="/contact" element={<LegalPage />} />

              {/* Public auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Protected dashboard routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout><Dashboard /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/accounts" element={
                <ProtectedRoute>
                  <AppLayout><Accounts /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/my-eas" element={
                <ProtectedRoute>
                  <AppLayout><MyEAs /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/magic-numbers" element={
                <ProtectedRoute>
                  <AppLayout><MagicNumbers /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/trades" element={
                <ProtectedRoute>
                  <AppLayout><Trades /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute>
                  <AppLayout><Reports /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/expenses" element={
                <ProtectedRoute>
                  <AppLayout><ExpenseTracker /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <AppLayout><Settings /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings/api-keys" element={
                <ProtectedRoute>
                  <AppLayout><ApiKeys /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings/ea-download" element={
                <ProtectedRoute>
                  <AppLayout><EaDownload /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings/upgrade" element={
                <ProtectedRoute>
                  <AppLayout><Upgrade /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings/payment-history" element={
                <ProtectedRoute>
                  <AppLayout><PaymentHistory /></AppLayout>
                </ProtectedRoute>
              } />

              {/* Admin routes */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminLayout><AdminDashboard /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/users" element={
                <AdminRoute>
                  <AdminLayout><AdminUsers /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/users/:id" element={
                <AdminRoute>
                  <AdminLayout><AdminUserDetail /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/billing" element={
                <AdminRoute>
                  <AdminLayout><AdminBilling /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/api-keys" element={
                <AdminRoute>
                  <AdminLayout><AdminApiKeys /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/announcements" element={
                <AdminRoute>
                  <AdminLayout><AdminAnnouncements /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/logs" element={
                <AdminRoute>
                  <AdminLayout><AdminLogs /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/feature-flags" element={
                <AdminRoute>
                  <AdminLayout><AdminFeatureFlags /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/pricing" element={
                <AdminRoute>
                  <AdminLayout><AdminPricing /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/wallets" element={
                <AdminRoute>
                  <AdminLayout><AdminWallets /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/payments" element={
                <AdminRoute>
                  <AdminLayout><AdminPayments /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/coupons" element={
                <AdminRoute>
                  <AdminLayout><AdminCoupons /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/ea-store" element={
                <AdminRoute>
                  <AdminLayout><AdminEAStore /></AdminLayout>
                </AdminRoute>
              } />
            </Routes>
          </ErrorBoundary>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: 'var(--bg-elevated, #1a1e38)',
                color: 'var(--text-primary, #e6edf3)',
                border: '1px solid var(--border-color, rgba(255,255,255,0.08))',
                borderRadius: '12px',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              },
              success: { iconTheme: { primary: 'var(--accent-primary, #10b981)', secondary: 'var(--bg-deepest, #070912)' } },
              error: { iconTheme: { primary: '#ef4444', secondary: 'var(--bg-deepest, #070912)' } },
            }}
          />
        </BrowserRouter>
      </WebSocketProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;



